import Dialog from '@features/components/Dialog/Dialog';
import { useServiceWorkerUpdate } from '@shared/hooks/useServiceWorkerUpdate';
import { type FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Service Worker Update Notification Component
 * Automatically forces update when a new version is available
 */
export const ServiceWorkerUpdateNotification: FC = () => {
  const { t } = useTranslation();
  const { isUpdateAvailable, isUpdating, currentVersion, error, forceUpdate, checkForUpdate } =
    useServiceWorkerUpdate();

  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const updateStartedRef = useRef(false);

  // Automatically trigger force update when an update is available
  useEffect(() => {
    if (isUpdateAvailable && !isUpdating && !updateStartedRef.current) {
      updateStartedRef.current = true;
      setIsDialogVisible(true);
      forceUpdate();
    }
  }, [isUpdateAvailable, isUpdating, forceUpdate]);

  // Handle update completion with delay
  useEffect(() => {
    if (updateStartedRef.current && !isUpdating && !isUpdateAvailable) {
      // Update completed, show success message
      setShowSuccess(true);

      // Wait 2 seconds before closing dialog
      const closeTimer = setTimeout(() => {
        setIsDialogVisible(false);
      }, 2000);

      return () => clearTimeout(closeTimer);
    }
  }, [isUpdating, isUpdateAvailable]);

  // Reload page only after dialog is completely hidden
  useEffect(() => {
    if (!isDialogVisible && updateStartedRef.current && showSuccess) {
      // Wait a brief moment for dialog close animation, then reload
      const reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 300);

      return () => clearTimeout(reloadTimer);
    }
  }, [isDialogVisible, showSuccess]);

  return (
    <>
      {/* Update Dialog */}
      <Dialog
        id="service-worker-update-dialog"
        title={t('serviceWorker.updateDescription', { defaultValue: 'Updating App' })}
        loading={isUpdating}
        visible={isDialogVisible}
        onClickOutside={() => {}} // No-op, user cannot close
        hideCloseButton={true}
      >
        <div className="flex flex-col gap-4 items-center justify-center py-8">
          {showSuccess ? (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-success"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-center">
                <p className="text-lg font-semibold text-success">
                  {t('serviceWorker.updateComplete', {
                    defaultValue: 'Update complete!',
                  })}
                </p>
                <p className="text-sm opacity-70 mt-2">
                  {t('serviceWorker.reloading', {
                    defaultValue: 'Reloading app...',
                  })}
                </p>
              </div>
            </>
          ) : (
            <>
              <span className="loading loading-spinner loading-xl text-primary"></span>
              <div className="text-center">
                <p className="text-lg mb-2">
                  {t('serviceWorker.updating', {
                    defaultValue: 'Installing new version, please wait...',
                  })}
                </p>
                {currentVersion && (
                  <p className="text-sm opacity-70">
                    {t('serviceWorker.currentVersion', {
                      defaultValue: 'Current: {version}',
                      version: currentVersion,
                    })}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Error Notification */}
      {error && (
        <div className="fixed bottom-20 right-4 z-50 max-w-sm">
          <div className="alert alert-error shadow-lg">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 shrink-0 stroke-current"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="font-bold">
                    {t('serviceWorker.updateError', { defaultValue: 'Update Error' })}
                  </h3>
                  <p className="text-xs">{error}</p>
                </div>
              </div>

              <button className="btn btn-sm btn-outline" onClick={checkForUpdate}>
                {t('serviceWorker.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Debug panel for Service Worker development
 * Only shown in development mode
 */
export const ServiceWorkerDebugPanel: FC = () => {
  const {
    isUpdateAvailable,
    isUpdating,
    currentVersion,
    isInstalled,
    error,
    checkForUpdate,
    forceUpdate,
  } = useServiceWorkerUpdate();

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 z-50 max-w-sm">
      <div className="collapse collapse-arrow bg-base-200 shadow-lg">
        <input type="checkbox" />
        <div className="collapse-title text-sm font-medium">
          🔧 SW Debug ({currentVersion || 'unknown'})
        </div>
        <div className="collapse-content">
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span>Installed:</span>
              <span className={isInstalled ? 'text-success' : 'text-error'}>
                {isInstalled ? '✅' : '❌'}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Update Available:</span>
              <span className={isUpdateAvailable ? 'text-warning' : 'text-success'}>
                {isUpdateAvailable ? '🔄' : '✅'}
              </span>
            </div>

            <div className="flex justify-between">
              <span>Updating:</span>
              <span className={isUpdating ? 'text-info' : ''}>{isUpdating ? '⏳' : '✅'}</span>
            </div>

            {error && <div className="text-error wrap-break-word">Error: {error}</div>}

            <div className="flex gap-1 pt-2">
              <button
                className="btn btn-xs btn-primary"
                onClick={checkForUpdate}
                disabled={isUpdating}
              >
                Check
              </button>

              <button
                className="btn btn-xs btn-warning"
                onClick={forceUpdate}
                disabled={isUpdating}
              >
                Force
              </button>

              <button
                className="btn btn-xs btn-error"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
              >
                Nuclear
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
